import { BRAND } from './brand';

/**
 * SaaS billing provider selection and customer-facing copy.
 * Switch providers with BILLING_PROVIDER=paddle|dodo|stripe in wrangler vars.
 * Legacy Renewal Drive billing — only relevant when LEGACY_RENEWAL_ENABLED is on.
 */
export type BillingProvider = 'paddle' | 'dodo' | 'stripe';

type EnvGetter = Record<string, unknown> | ((key: string) => string | undefined);

function makeGetter(env: EnvGetter): (key: string) => string | undefined {
  return typeof env === 'function'
    ? env
    : (key: string) => {
        const v = env[key];
        return typeof v === 'string' && v.length ? v : undefined;
      };
}

/** Active billing provider for SaaS subscriptions (default: paddle). */
export function resolveBillingProvider(env: EnvGetter): BillingProvider {
  const get = makeGetter(env);
  const raw = (get('BILLING_PROVIDER') ?? 'paddle').toLowerCase();
  if (raw === 'dodo') return 'dodo';
  if (raw === 'stripe') return 'stripe';
  return 'paddle';
}

export interface BillingProviderCopy {
  provider: BillingProvider;
  morLegalName: string;
  morShortName: string;
  isMerchantOfRecord: boolean;
  buyerTermsUrl: string;
  statementDescriptor: string;
  portalName: string;
  termsSectionTitle: string;
  footerSubscriptionLine: string;
  landingBillingLine: string;
  subprocessorsName: string;
  receiptLabel: string;
  integrationsLabel: string;
  privacyProcessorLine: string;
  cookieEmbedLine: string;
  checkoutEmbedLine: string;
}

const PADDLE_COPY: BillingProviderCopy = {
  provider: 'paddle',
  morLegalName: 'Paddle.com Market Limited',
  morShortName: 'Paddle',
  isMerchantOfRecord: true,
  buyerTermsUrl: 'https://www.paddle.com/legal/checkout-buyer-terms',
  statementDescriptor: 'Paddle or Paddle.com',
  portalName: 'Paddle customer portal',
  termsSectionTitle: 'Subscriptions, billing & Paddle as Merchant of Record',
  footerSubscriptionLine: `Your ${BRAND.name} plan is billed by Paddle. We do not process payments for your customers.`,
  landingBillingLine: `Your ${BRAND.name} subscription is billed by Paddle as Merchant of Record. We do not process, hold, or transmit payments on behalf of your shop or your customers.`,
  subprocessorsName: 'Paddle.com Market Limited',
  receiptLabel: 'Paddle receipt',
  integrationsLabel: `Paddle for your ${BRAND.name} subscription billing`,
  privacyProcessorLine:
    'Paddle processes payments as Merchant of Record. We receive subscription status, plan tier, Paddle customer id, and billing events to enforce plan limits.',
  cookieEmbedLine:
    'Paddle may set cookies when you open the billing portal or subscription checkout. Those providers\' policies apply on their domains.',
  checkoutEmbedLine: 'Paddle checkout',
};

const DODO_COPY: BillingProviderCopy = {
  provider: 'dodo',
  morLegalName: 'Dodo Payments',
  morShortName: 'Dodo Payments',
  isMerchantOfRecord: true,
  buyerTermsUrl: 'https://dodopayments.com/terms-of-service',
  statementDescriptor: 'Dodo Payments',
  portalName: 'Dodo Payments customer portal',
  termsSectionTitle: 'Subscriptions, billing & Dodo Payments as Merchant of Record',
  footerSubscriptionLine: `Your ${BRAND.name} plan is billed by Dodo Payments. We do not process payments for your customers.`,
  landingBillingLine: `Your ${BRAND.name} subscription is billed by Dodo Payments as Merchant of Record. We do not process, hold, or transmit payments on behalf of your shop or your customers.`,
  subprocessorsName: 'Dodo Payments',
  receiptLabel: 'Dodo Payments receipt',
  integrationsLabel: `Dodo Payments for your ${BRAND.name} subscription billing`,
  privacyProcessorLine:
    'Dodo Payments processes payments as Merchant of Record. We receive subscription status, plan tier, Dodo customer id, and billing events to enforce plan limits.',
  cookieEmbedLine:
    'Dodo Payments may set cookies when you open the billing portal or subscription checkout. Those providers\' policies apply on their domains.',
  checkoutEmbedLine: 'Dodo Payments checkout',
};

const STRIPE_COPY: BillingProviderCopy = {
  provider: 'stripe',
  morLegalName: 'Stripe, Inc.',
  morShortName: 'Stripe',
  isMerchantOfRecord: false,
  buyerTermsUrl: 'https://stripe.com/legal/consumer',
  statementDescriptor: 'Stripe',
  portalName: 'Stripe billing portal',
  termsSectionTitle: 'Subscriptions & billing',
  footerSubscriptionLine: `Your ${BRAND.name} plan is billed via Stripe. We do not process payments for your customers.`,
  landingBillingLine: `Your ${BRAND.name} subscription is billed via Stripe. We do not process, hold, or transmit payments on behalf of your shop or your customers.`,
  subprocessorsName: 'Stripe, Inc.',
  receiptLabel: 'Stripe receipt',
  integrationsLabel: `Stripe for your ${BRAND.name} subscription billing`,
  privacyProcessorLine:
    'Stripe processes subscription payments. We receive subscription status, plan tier, and billing events to enforce plan limits.',
  cookieEmbedLine:
    'Stripe may set cookies when you open the billing portal or subscription checkout. Those providers\' policies apply on their domains.',
  checkoutEmbedLine: 'Stripe checkout',
};

export function getBillingProviderCopy(provider: BillingProvider): BillingProviderCopy {
  if (provider === 'dodo') return DODO_COPY;
  if (provider === 'stripe') return STRIPE_COPY;
  return PADDLE_COPY;
}

export function resolveBillingProviderCopy(env: EnvGetter): BillingProviderCopy {
  return getBillingProviderCopy(resolveBillingProvider(env));
}

/** Env var holding the plan product/price id for the active billing provider. */
export function planPriceEnvKey(provider: BillingProvider, tier: string): string {
  if (provider === 'dodo') {
    const map: Record<string, string> = {
      lite: 'DODO_PRODUCT_LITE',
      starter: 'DODO_PRODUCT_STARTER',
      growth: 'DODO_PRODUCT_GROWTH',
      pro: 'DODO_PRODUCT_PRO',
    };
    return map[tier] ?? `DODO_PRODUCT_${tier.toUpperCase()}`;
  }
  if (provider === 'stripe') {
    const map: Record<string, string> = {
      lite: 'STRIPE_LITE_PRICE_ID',
      starter: 'STRIPE_STARTER_PRICE_ID',
      growth: 'STRIPE_GROWTH_PRICE_ID',
      pro: 'STRIPE_PRO_PRICE_ID',
    };
    return map[tier] ?? `STRIPE_${tier.toUpperCase()}_PRICE_ID`;
  }
  const map: Record<string, string> = {
    lite: 'PADDLE_PRICE_LITE',
    starter: 'PADDLE_PRICE_STARTER',
    growth: 'PADDLE_PRICE_GROWTH',
    pro: 'PADDLE_PRICE_PRO',
  };
  return map[tier] ?? `PADDLE_PRICE_${tier.toUpperCase()}`;
}
